/**
 * Was ein Spieler in einem laufenden Zug sehen und tun darf — abgeleitet, nicht entschieden.
 *
 * **Die Regel kommt aus der Engine.** Welche Aktionen legal sind, rechnet `getLegalActions` —
 * dieselbe Funktion, mit der der Server die eingehende Absicht prüft, und dasselbe Paket im
 * Browser (`ARCHITECTURE.md` **E26**). Hier wird nichts nachgerechnet, nur zusammengestellt.
 *
 * **Der Client zeigt, der Server entscheidet** (`CLAUDE.md` Regel 1). Jede Absicht wird
 * serverseitig erneut geprüft, und zwar **zweimal**: bei der Preview und beim Commit (§34, §59).
 * Wäre diese Rechnung falsch, bekäme der Spieler ein `ILLEGAL_ACTION` — und keine falsche Partie.
 *
 * **Hervorhebungen gibt es nur am Zug** (`OPEN_QUESTIONS.md` Nr. 36, entschieden 2026-10-04) —
 * auch für die eigenen Figuren. §50 regelt ausdrücklich nur den Spieler am Zug. Deshalb ist die
 * Aktionsliste für jeden anderen **leer** und nicht nur ungezeigt: eine Liste, die da ist, wird
 * irgendwann gezeigt.
 *
 * **Gewählt wird eine Action ID, nie eine Koordinate** (§74, eine der vier KI-Nahtstellen). Die
 * ID steht in der Aktion, und genau sie geht in `SET_PREVIEW` und `COMMIT`. Eine zweite
 * Berechnung aus Feldern gäbe es damit nicht — und eine fremde ID ist nicht einschleusbar,
 * weil der Server sie in seiner **eigenen** Liste nachschlägt (§110).
 *
 * **Figur und Zielfeld bestimmen die Aktion eindeutig.** Darauf beruht die Bedienung „Figur
 * wählen, Feld klicken". Das ist keine Annahme, sondern folgt aus den Regeln: MOVE führt auf ein
 * freies Feld, CAPTURE und PUSH auf ein besetztes, RESPAWN gilt für eine Figur ohne Feld — und
 * keine Figurenklasse schlägt *und* stößt (dem Stoßer fehlt `capture`, allen anderen `push`).
 * `actionsAt` gibt trotzdem eine **Liste** zurück: so bleibt die Stelle sichtbar, an der eine
 * künftige Regeländerung eine Auswahl nötig machen würde, statt still eine Variante zu
 * bevorzugen. Ein Test hält die Eindeutigkeit an einer vollen Stellung fest.
 *
 * **Fail closed:** wer nicht mitspielt, bekommt eine leere Sicht.
 */

import {
  capturedOf,
  createBoard,
  createSectorLayout,
  getLegalActions,
  hex,
  hexEquals,
  isActivePlayer,
  piecesOf,
  playerOf,
  ruleContextOf,
  type CapturedPiece,
  type GameState,
  type Hex,
  type LegalAction,
  type PlacedPiece,
} from '@flaghex/engine';

export interface TurnView {
  /** Ist dieser Spieler am Zug — **und** läuft die Partie? */
  readonly active: boolean;
  /** Seine legalen Aktionen. **Leer**, wenn er nicht am Zug ist (Nr. 36). */
  readonly actions: readonly LegalAction[];
  /** Seine Figuren auf dem Brett. Ab `PLAYING` sieht er auch die fremden, hier stehen nur eigene. */
  readonly placed: readonly PlacedPiece[];
  /** Seine Figuren im Respawn-Pool (§22) — geschlagen und wieder einsetzbar. */
  readonly pool: readonly CapturedPiece[];
}

const EMPTY: TurnView = { active: false, actions: [], placed: [], pool: [] };

export function turnOf(state: GameState, me: string): TurnView {
  if (playerOf(state, me) === undefined) {
    return EMPTY;
  }

  const placed = piecesOf(state.boardState, me);
  const pool = capturedOf(state.boardState, me);

  // Nur in `PLAYING`: in `SETUP` wird gestellt und nicht gezogen, in `PAUSED` weist der Server
  // jede Preview ab (§40), und nach `FINISHED` gibt es keinen Zug mehr (§47).
  const active = state.status === 'PLAYING' && isActivePlayer(state, me);

  return {
    active,
    actions: active ? getLegalActions(contextOf(state), me) : [],
    placed,
    pool,
  };
}

/** Die Aktionen einer Figur — Brettfigur oder Vorratsfigur, derselbe Weg (§22, §74). */
export function actionsOfPiece(view: TurnView, pieceId: string): readonly LegalAction[] {
  return view.actions.filter((action) => action.piece === pieceId);
}

/**
 * Die Zielfelder dieser Aktionen — mit ihrer **Art**.
 *
 * §50 verlangt ausdrücklich, CAPTURE-Felder und PUSH-Möglichkeiten **unterscheidbar**
 * anzuzeigen. Die Art muss deshalb bis zum Brett durchkommen: eine einzige Markierung sagte,
 * *dass* dort etwas geht, nicht *was*.
 */
export function highlightsOf(actions: readonly LegalAction[]): readonly ActionHighlight[] {
  return actions.map((action) => ({
    at: action.to,
    kind: action.type,
    // Nur der Stoß hat eine Richtung: `to` ist beim Stoß das Feld, auf dem das Opfer **steht** (dort
    // klickt man), `toward` das, auf dem es landet. Ohne beides zeigt die Markierung ein Ziel,
    // aber nicht die Bewegung dorthin — und genau die unterscheidet den Stoß (§18).
    //
    // **Bis `R16` stand hier `from: action.victimAt`** — dasselbe Feld wie `to`, ein Pfeil der
    // Länge null. Der Kommentar dazu hielt `to` für das Landefeld.
    ...(action.type === 'PUSH' ? { toward: pushLanding(action) } : {}),
  }));
}

/**
 * Wohin ein Stoß die Figur bringt — beim Stoß über den Rand das erste Feld jenseits des Bretts,
 * in Stoßrichtung. Eine Definition für das Spiel und die Anleitung (`apps/web/manual`).
 *
 * Die Richtung ist die vom Stoßer zum Opfer: ein Stoß geht immer entlang einer Hex-Linie (§18),
 * der Abstand geteilt durch die Hex-Distanz ist deshalb eine der sechs Einheitsrichtungen.
 */
export function pushLanding(action: Extract<LegalAction, { type: 'PUSH' }>): Hex {
  if (action.victimTo !== undefined) return action.victimTo;
  const dq = action.victimAt.q - action.from.q;
  const dr = action.victimAt.r - action.from.r;
  const distance = Math.max(Math.abs(dq), Math.abs(dr), Math.abs(dq + dr));
  return hex(action.victimAt.q + dq / distance, action.victimAt.r + dr / distance);
}

export interface ActionHighlight {
  readonly at: Hex;
  readonly kind: LegalAction['type'];
  /** Wohin die gestoßene Figur kommt — nur beim Stoß belegt, siehe `highlightsOf`. */
  readonly toward?: Hex | undefined;
}

/**
 * Die Aktionen, die auf diesem Feld liegen.
 *
 * Eine Liste, obwohl es mit den heutigen Regeln nie mehr als eine ist — siehe Modulkopf.
 */
export function actionsAt(actions: readonly LegalAction[], cell: Hex): readonly LegalAction[] {
  return actions.filter((action) => hexEquals(action.to, cell));
}

/**
 * Der Regelkontext dieser Stellung.
 *
 * Brett und Layout entstehen dabei neu — derselbe Hinweis wie in `setup.ts`: vertretbar für
 * einen Bildschirm, der einmal je Zustandsänderung rechnet, aber der Grund, warum die
 * aufrufende Komponente das Ergebnis merken sollte.
 */
function contextOf(state: GameState) {
  const board = createBoard({ sideLength: state.ruleConfig.board.sideLength });
  return ruleContextOf(state, board, createSectorLayout(board));
}
